import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../../config/api';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;

const BackButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: #5a6268;
  }
`;

const StatusSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const StatusSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  margin-right: 1rem;
`;

const UpdateButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: #5a6fd8;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  margin-left: 1rem;
  
  &:hover {
    background: #c82333;
  }
`;

// Document Management Styled Components
const DocumentUploadArea = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: #cbd5e0;
  }
`;

const UploadTitle = styled.h3`
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1.5rem 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '📤';
    font-size: 1.2rem;
  }
`;

const DocumentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #4a5568;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: 0.75rem 1rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #4a5568;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: #667eea;
    background: #edf2f7;
    color: #667eea;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  left: -9999px;
  opacity: 0;
`;

const FileName = styled.div`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #4a5568;
  background: #f7fafc;
  min-height: 1.2rem;
  display: flex;
  align-items: center;
`;

const UploadButton = styled.button`
  background: #48bb78;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  min-width: 140px;
  
  &:hover:not(:disabled) {
    background: #38a169;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const DocumentList = styled.div`
  margin-top: 1.5rem;
`;

const DocumentItem = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: #cbd5e0;
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const DocumentIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
`;

const DocumentDetails = styled.div`
  flex: 1;
`;

const DocumentName = styled.h3`
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  line-height: 1.4;
`;

const DocumentMeta = styled.div`
  font-size: 0.9rem;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background: #cbd5e0;
    border-radius: 50%;
  }
  
  &:first-child::before {
    display: none;
  }
`;

const DocumentActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;

  &:not(:disabled) {
    background: #667eea;
    color: white;

    &:hover {
      background: #5a6fd8;
      transform: translateY(-1px);
    }
  }

  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
  }

  &.approve {
    background: #48bb78;
    &:hover:not(:disabled) { 
      background: #38a169;
      transform: translateY(-1px);
    }
  }
  
  &.reject {
    background: #f56565;
    &:hover:not(:disabled) { 
      background: #e53e3e;
      transform: translateY(-1px);
    }
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 0.5rem;
  
  ${props => {
    switch (props.status) {
      case 'uploaded_by_admin':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'uploaded_by_user':
        return 'background: #fff3e0; color: #f57c00;';
      case 'completed':
        return 'background: #e8f5e8; color: #2e7d32;';
      default:
        return 'background: #f5f5f5; color: #666;';
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CommentsInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  resize: vertical;
  margin: 0.5rem 0;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ApprovalSection = styled.div`
  background: #f8fafc;
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    background: #f1f5f9;
  }
`;

const ApprovalTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.75rem;
`;

const ApprovalControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ApprovalButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ModalTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const ModalMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #ddd;
  background: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const DeleteConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: #dc3545;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #c82333;
  }
`;

const PaymentLinkInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  margin-right: 0.5rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const PaymentLinkButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const DetailSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  font-weight: 500;
  color: #333;
`;

const SectionContent = styled.div`
  padding: 1.5rem;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1rem;
  align-items: start;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const DataLabel = styled.div`
  font-weight: 500;
  color: #555;
`;

const DataValue = styled.div`
  color: #333;
`;

const FileLink = styled.a`
  color: #667eea;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OwnerCard = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #f8f9fa;
`;

const OwnerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
`;

const OwnerName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
`;

const OwnerShare = styled.div`
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const OwnerDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OwnerDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.div`
  color: #333;
`;

interface RegistrationDetail {
  id: number;
  user: {
    id: number;
    email: string;
    phone: string;
    created_at: string;
  };
  status: string;
  company_type?: string;
  country?: string;
  business_activity_code?: string;
  business_activity?: string;
  business_description?: string;
  company_name_armenian?: string;
  company_name_english?: string;
  company_name_russian?: string;
  owners_data?: string;
  authorized_capital?: number;
  decision_making?: string;
  charter_preferences?: string;
  additional_services?: string;
  additional_comments?: string;
  preferred_communication?: string;
  payment_link?: string;
  created_at: string;
  updated_at: string;
}

interface Owner {
  full_name: string;
  address: string;
  passport_scan_path?: string;
  additional_proof_path?: string;
  share_percentage: number;
}

const AdminRegistrationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [registration, setRegistration] = useState<RegistrationDetail | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  
  // Document management state
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('contract');
  const [documentName, setDocumentName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [approvalComments, setApprovalComments] = useState<{[key: number]: string}>({});

  // Function to handle secure file downloads
  const handleFileDownload = async (filename: string, documentType: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required to download files');
        return;
      }

      const response = await axios.get(
        getCurrentApiEndpoints().ADMIN_DOWNLOAD_FILE(filename),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const fetchRegistration = useCallback(async () => {
    try {
      const response = await axios.get(getCurrentApiEndpoints().ADMIN_REGISTRATION_BY_ID(parseInt(id!)));
      setRegistration(response.data);
      setNewStatus(response.data.status);
      setPaymentLink(response.data.payment_link || '');
    } catch (error) {
      console.error('Failed to fetch registration:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchRegistration();
    }
  }, [id, fetchRegistration]);

  // Load documents when registration is available
  useEffect(() => {
    if (registration) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration]);

  const handleStatusUpdate = async () => {
    if (!registration || newStatus === registration.status) return;

    setIsUpdating(true);
    try {
      await axios.put(getCurrentApiEndpoints().UPDATE_REGISTRATION_STATUS(parseInt(id!)), {
        status: newStatus
      });
      
      setRegistration(prev => prev ? { ...prev, status: newStatus } : null);
      alert(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePaymentLinkUpdate = async () => {
    if (!registration) return;
    
    setIsUpdatingPayment(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        getCurrentApiEndpoints().UPDATE_PAYMENT_LINK(registration.id),
        { payment_link: paymentLink },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRegistration(prev => prev ? { ...prev, payment_link: paymentLink } : null);
      alert('Payment link updated successfully');
    } catch (error) {
      console.error('Failed to update payment link:', error);
      alert('Failed to update payment link');
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleDeleteRegistration = async () => {
    if (!registration) return;

    setIsDeleting(true);
    try {
      await axios.delete(getCurrentApiEndpoints().DELETE_REGISTRATION(parseInt(id!)));
      alert('Registration deleted successfully!');
      navigate('/admin/registrations');
    } catch (error) {
      console.error('Failed to delete registration:', error);
      alert('Failed to delete registration');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Document management functions
  const loadDocuments = useCallback(async () => {
    if (!registration) return;
    
    setIsLoadingDocuments(true);
    try {
      const response = await axios.get(getCurrentApiEndpoints().ADMIN_GET_DOCUMENTS(registration.id));
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, [registration]);

  const handleDocumentUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registration || !selectedFile) return;

    setIsUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);
      formData.append('document_name', documentName);

      await axios.post(
        getCurrentApiEndpoints().ADMIN_UPLOAD_DOCUMENT(registration.id),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Reset form
      setDocumentName('');
      setSelectedFile(null);
      setDocumentType('contract');
      
      // Reload documents and registration
      await loadDocuments();
      await fetchRegistration();
      
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document');
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const handleDocumentDownload = async (documentId: number) => {
    if (!registration) return;

    try {
      const response = await axios.get(
        getCurrentApiEndpoints().ADMIN_DOWNLOAD_DOCUMENT(registration.id, documentId),
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `document_${documentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document');
    }
  };

  const handleDocumentApproval = async (documentId: number, approved: boolean) => {
    if (!registration) return;

    try {
      await axios.put(
        getCurrentApiEndpoints().ADMIN_APPROVE_DOCUMENT(registration.id, documentId),
        { 
          approved,
          comments: approvalComments[documentId] || null
        }
      );

      // Clear comments for this document
      setApprovalComments(prev => ({ ...prev, [documentId]: '' }));

      // Reload documents and registration
      await loadDocuments();
      await fetchRegistration();
      
      alert(`Document ${approved ? 'approved' : 'rejected'} successfully!`);
    } catch (error) {
      console.error('Failed to update document approval:', error);
      alert('Failed to update document approval');
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'uploaded_by_admin':
        return 'Uploaded by Admin';
      case 'uploaded_by_user':
        return 'Uploaded by User';
      case 'completed':
        return 'Completed';
      default:
        return 'Pending';
    }
  };

  const parseOwners = (ownersData: string | null | undefined): Owner[] => {
    try {
      return ownersData ? JSON.parse(ownersData) : [];
    } catch {
      return [];
    }
  };

  const parseJson = (jsonString: string | null | undefined) => {
    if (!jsonString) return {};
    try {
      return JSON.parse(jsonString);
    } catch {
      return {};
    }
  };

  if (isLoading) {
    return <div>Loading registration details...</div>;
  }

  if (!registration) {
    return <div>Registration not found</div>;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Submitted';
      case 'awaiting_payment': return 'Awaiting Payment';
      case 'payment_verification': return 'Payment Verification';
      case 'in_progress': return 'In Progress';
      case 'awaiting_documents': return 'Awaiting Documents';
      case 'awaiting_signatures': return 'Awaiting Signatures';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const owners = parseOwners(registration.owners_data);
  const services = parseJson(registration.additional_services);
  
  // Helper function to parse and format charter preferences
  const parseCharterPreferences = (preferencesJson: string) => {
    try {
      const prefs = JSON.parse(preferencesJson);
      const preferenceLabels: Record<string, string> = {
        allow_electronic_meetings: 'Allow Electronic Meetings',
        require_board_approval: 'Require Board Approval for Major Transactions',
        preemptive_rights: 'Preemptive Rights',
        drag_along_rights: 'Drag-Along Rights',
        tag_along_rights: 'Tag-Along Rights'
      };
      
      return Object.entries(prefs)
        .filter(([_, value]) => value === true)
        .map(([key, _]) => preferenceLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    } catch {
      return [];
    }
  };
  
  const charterPreferences = parseCharterPreferences(registration.charter_preferences || '{}');

  const selectedServices = Object.entries(services)
    .filter(([_, selected]) => selected)
    .map(([key, _]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

  return (
    <Container>
      <Header>
        <Title>Registration #{registration.id}</Title>
        <BackButton onClick={() => navigate('/admin/registrations')}>
          ← Back to Registrations
        </BackButton>
      </Header>

      <DetailSection>
        <SectionHeader>Status Management</SectionHeader>
        <SectionContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.2rem' }}>
                Current Status
              </h3>
              <StatusBadge status={registration.status}>
                {getStatusLabel(registration.status)}
              </StatusBadge>
            </div>
          </div>
          
          <div style={{ 
            background: '#f8f9fa', 
            borderRadius: '6px', 
            padding: '1.5rem',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem' }}>
              Update Status
            </h4>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <StatusSelect
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="awaiting_payment">Awaiting Payment</option>
                  <option value="payment_verification">Payment Verification</option>
                  <option value="in_progress">In Progress</option>
                  <option value="awaiting_documents">Awaiting Documents</option>
                  <option value="awaiting_signatures">Awaiting Signatures</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </StatusSelect>
              </div>
              <UpdateButton 
                onClick={handleStatusUpdate}
                disabled={isUpdating || newStatus === registration.status}
              >
                {isUpdating ? 'Updating...' : 'Update Status'}
              </UpdateButton>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'flex-end',
            marginTop: '1.5rem'
          }}>
            <DeleteButton onClick={() => setShowDeleteConfirmation(true)}>
              Delete Registration
            </DeleteButton>
          </div>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Payment Link Management</SectionHeader>
        <SectionContent>
          <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.9rem' }}>
            Set a payment link for users when status is "Awaiting Payment"
          </p>
          
          <div style={{ 
            background: '#f8f9fa', 
            borderRadius: '6px', 
            padding: '1.5rem',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem' }}>
              Payment URL
            </h4>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '300px' }}>
                <PaymentLinkInput
                  type="url"
                  placeholder="Enter payment URL (e.g., https://payment.example.com/pay/123)"
                  value={paymentLink}
                  onChange={(e) => setPaymentLink(e.target.value)}
                />
              </div>
              <PaymentLinkButton 
                onClick={handlePaymentLinkUpdate}
                disabled={isUpdatingPayment}
              >
                {isUpdatingPayment ? 'Updating...' : 'Update Payment Link'}
              </PaymentLinkButton>
            </div>
          </div>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Document Management</SectionHeader>
        <SectionContent>
          <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.9rem' }}>
            Upload documents for users to sign and manage the document exchange workflow
          </p>
          
          {/* Document Upload Form */}
          <DocumentUploadArea>
            <UploadTitle>Upload New Document</UploadTitle>
            <DocumentForm onSubmit={handleDocumentUpload}>
              <FormRow>
                <FormGroup>
                  <Label>Document Type</Label>
                  <Select 
                    value={documentType} 
                    onChange={(e) => setDocumentType(e.target.value)}
                  >
                    <option value="contract">Contract</option>
                    <option value="charter">Charter</option>
                    <option value="agreement">Agreement</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Document Name</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Company Registration Contract"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>PDF File</Label>
                  <FileInputWrapper>
                    <FileInputLabel htmlFor="admin-file-input">
                      Choose File
                    </FileInputLabel>
                    <HiddenFileInput
                      id="admin-file-input"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      required
                    />
                    <FileName>
                      {selectedFile ? selectedFile.name : 'No file selected'}
                    </FileName>
                  </FileInputWrapper>
                </FormGroup>
                <FormGroup>
                  <UploadButton 
                    type="submit" 
                    disabled={isUploadingDocument || !selectedFile || !documentName}
                  >
                    {isUploadingDocument ? (
                      <>
                        <LoadingSpinner />
                        Uploading...
                      </>
                    ) : (
                      'Upload Document'
                    )}
                  </UploadButton>
                </FormGroup>
              </FormRow>
            </DocumentForm>
          </DocumentUploadArea>

          {/* Document List */}
          <DocumentList>
            <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem' }}>
              Documents ({documents.length})
            </h4>
            
            {isLoadingDocuments ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <LoadingSpinner />
                Loading documents...
              </div>
            ) : documents.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem', 
                color: '#666',
                background: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                No documents uploaded yet
              </div>
            ) : (
              documents.map((doc) => (
                <DocumentItem key={doc.id}>
                  <DocumentHeader>
                    <DocumentInfo>
                      <DocumentIcon>📄</DocumentIcon>
                      <DocumentDetails>
                        <DocumentName>{doc.document_name}</DocumentName>
                        <DocumentMeta>
                          <MetaItem>{doc.document_type}</MetaItem>
                          <MetaItem>{new Date(doc.created_at).toLocaleDateString()}</MetaItem>
                          <MetaItem>{doc.uploaded_by_admin ? 'By Admin' : 'By User'}</MetaItem>
                        </DocumentMeta>
                      </DocumentDetails>
                    </DocumentInfo>
                    <DocumentActions>
                      <StatusBadge status={doc.status}>
                        {getStatusDisplayName(doc.status)}
                      </StatusBadge>
                      <ActionButton onClick={() => handleDocumentDownload(doc.id)}>
                        Download
                      </ActionButton>
                    </DocumentActions>
                  </DocumentHeader>
                  
                  {/* Show approval section for documents uploaded by user */}
                  {doc.status === 'uploaded_by_user' && (
                    <ApprovalSection>
                      <ApprovalTitle>Review and Approve Document</ApprovalTitle>
                      <ApprovalControls>
                        <CommentsInput
                          placeholder="Add comments (optional)..."
                          value={approvalComments[doc.id] || ''}
                          onChange={(e) => setApprovalComments(prev => ({ ...prev, [doc.id]: e.target.value }))}
                        />
                        <ApprovalButtons>
                          <ActionButton 
                            className="approve"
                            onClick={() => handleDocumentApproval(doc.id, true)}
                          >
                            Approve
                          </ActionButton>
                          <ActionButton 
                            className="reject"
                            onClick={() => handleDocumentApproval(doc.id, false)}
                          >
                            Reject
                          </ActionButton>
                        </ApprovalButtons>
                      </ApprovalControls>
                    </ApprovalSection>
                  )}
                </DocumentItem>
              ))
            )}
          </DocumentList>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>User Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Email:</DataLabel>
            <DataValue>{registration.user.email}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Phone:</DataLabel>
            <DataValue>{registration.user.phone || 'Not provided'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>User Created:</DataLabel>
            <DataValue>{formatDate(registration.user.created_at)}</DataValue>
          </DataGrid>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Business Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Company Type:</DataLabel>
            <DataValue>{registration.company_type || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Country:</DataLabel>
            <DataValue>{registration.country || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Activity Code:</DataLabel>
            <DataValue>{registration.business_activity_code || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Activity:</DataLabel>
            <DataValue>{registration.business_activity || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Description:</DataLabel>
            <DataValue>{registration.business_description || 'Not specified'}</DataValue>
          </DataGrid>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Company Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Armenian Name:</DataLabel>
            <DataValue>{registration.company_name_armenian || 'Not specified'}</DataValue>
          </DataGrid>
          {registration.company_name_english && (
            <DataGrid>
              <DataLabel>English Name:</DataLabel>
              <DataValue>{registration.company_name_english}</DataValue>
            </DataGrid>
          )}
          {registration.company_name_russian && (
            <DataGrid>
              <DataLabel>Russian Name:</DataLabel>
              <DataValue>{registration.company_name_russian}</DataValue>
            </DataGrid>
          )}
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Owners Information & Share Distribution</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Authorized Capital:</DataLabel>
            <DataValue>{registration.authorized_capital ? `${registration.authorized_capital.toLocaleString()} AMD` : 'Not specified'}</DataValue>
          </DataGrid>
          
          <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
            <DataLabel style={{ marginBottom: '1rem' }}>Owners:</DataLabel>
            {owners.length > 0 ? (
              owners.map((owner, index) => (
                <OwnerCard key={index}>
                  <OwnerHeader>
                    <OwnerName>Owner {index + 1}: {owner.full_name}</OwnerName>
                    <OwnerShare>{owner.share_percentage}% Share</OwnerShare>
                  </OwnerHeader>
                  <OwnerDetails>
                    <OwnerDetail>
                      <DetailLabel>Address:</DetailLabel>
                      <DetailValue>{owner.address}</DetailValue>
                    </OwnerDetail>
                    <OwnerDetail>
                      <DetailLabel>Documents:</DetailLabel>
                      <DetailValue>
                        {owner.passport_scan_path ? (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <FileLink 
                              onClick={() => handleFileDownload(owner.passport_scan_path!, 'passport')}
                              style={{ cursor: 'pointer' }}
                            >
                              📄 View Passport Scan
                            </FileLink>
                          </div>
                        ) : (
                          <div style={{ color: '#dc3545', marginBottom: '0.5rem' }}>
                            ❌ Passport scan not uploaded
                          </div>
                        )}
                        {owner.additional_proof_path ? (
                          <div style={{ marginTop: '0.5rem' }}>
                            <FileLink 
                              onClick={() => handleFileDownload(owner.additional_proof_path!, 'additional_proof')}
                              style={{ cursor: 'pointer' }}
                            >
                              📄 View Additional Proof
                            </FileLink>
                          </div>
                        ) : (
                          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                            No additional proof uploaded
                          </div>
                        )}
                      </DetailValue>
                    </OwnerDetail>
                  </OwnerDetails>
                </OwnerCard>
              ))
            ) : (
              <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No owners information available
              </div>
            )}
          </div>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Charter Details</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Decision Making:</DataLabel>
            <DataValue>{registration.decision_making?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</DataValue>
          </DataGrid>
          {charterPreferences.length > 0 && (
            <DataGrid>
              <DataLabel>Additional Charter Preferences:</DataLabel>
              <DataValue>
                {charterPreferences.map((preference, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#28a745', marginRight: '0.5rem' }}>✓</span>
                    {preference}
                  </div>
                ))}
              </DataValue>
            </DataGrid>
          )}
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Additional Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Communication Method:</DataLabel>
            <DataValue>{registration.preferred_communication?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</DataValue>
          </DataGrid>
          {selectedServices.length > 0 && (
            <DataGrid>
              <DataLabel>Selected Services:</DataLabel>
              <DataValue>
                {selectedServices.map((service, index) => (
                  <div key={index}>• {service}</div>
                ))}
              </DataValue>
            </DataGrid>
          )}
          {registration.additional_comments && (
            <DataGrid>
              <DataLabel>Comments:</DataLabel>
              <DataValue>{registration.additional_comments}</DataValue>
            </DataGrid>
          )}
          {registration.payment_link && (
            <DataGrid>
              <DataLabel>Payment Link:</DataLabel>
              <DataValue>
                <a 
                  href={registration.payment_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#007bff', textDecoration: 'none' }}
                >
                  {registration.payment_link}
                </a>
              </DataValue>
            </DataGrid>
          )}
          <DataGrid>
            <DataLabel>Created:</DataLabel>
            <DataValue>{formatDate(registration.created_at)}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Last Updated:</DataLabel>
            <DataValue>{formatDate(registration.updated_at)}</DataValue>
          </DataGrid>
        </SectionContent>
      </DetailSection>

      {showDeleteConfirmation && (
        <Modal onClick={() => setShowDeleteConfirmation(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Confirm Deletion</ModalTitle>
            <ModalMessage>
              Are you sure you want to delete this registration? This action cannot be undone.
            </ModalMessage>
            <ModalButtons>
              <CancelButton onClick={() => setShowDeleteConfirmation(false)}>Cancel</CancelButton>
              <DeleteConfirmButton onClick={handleDeleteRegistration} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </DeleteConfirmButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminRegistrationDetail;